import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import request from "../../../api/request";
import { BackendAPI } from "../../../const/api";
import { AuthContext } from "../../../auth/authContext";
import { UserRole, USER_ROLE_STAFF_CREATABLE } from "../../../const/enum";
import md5 from "md5";
import "./CreateUser.css";
import { useToast } from "../../../component/ToastBar";

const defaultForm = {
  name: "",
  code: "",
  email: "",
  phone: "",
  password: "",
  passwordConfirm: "",
  role: "",
  abbreviation: "",
  salesperson: "",
  group: "",
  organization: "",
  paymentTerms: "",
  address: "",
  customer: true,
};
const CreateUser = () => {
  const toast = useToast();

  const authContext = useContext(AuthContext);

  const [error, setError] = useState("");

  const [customerType, setCustomerType] = useState(true);

  const [userForm, setUserForm] = useState(defaultForm);

  const [salesOptions, setSalesOptions] = useState<string[]>([]);

  useEffect(() => {
    const salesSelectOptions = async () => {
      const response = await request.get(BackendAPI.listSalesperson);
      if (!response.success) {
        return;
      }
      const items = response.data.map((sales) => {
        return sales;
      });
      setSalesOptions(items);
    };
    salesSelectOptions().then();
  }, [authContext]);

  const handleChangeType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerType((event.target as HTMLInputElement).value === "customer");
    setUserForm({ ...userForm, customer: event.target.value === "customer" });
  };

  const handleEmailChange = (em) => {
    setUserForm({ ...userForm, email: em });
    if (!validateEmail(em)) {
      setError("请输入有效的邮箱地址");
    } else {
      setError("");
    }
  };

  const validateEmail = (em) => {
    return /^\w+([-+.]\w+)*@\w+([-.]\w+)*.\w+([-.]\w+)*$/.test(em);
  };

  const handlePhoneChange = (p) => {
    setUserForm({ ...userForm, phone: p });
    if (!validatePhone(p)) {
      setError("请输入有效的手机号");
    } else {
      setError("");
    }
  };

  const validatePhone = (p) => {
    return /^(13[0-9]|14[0-9]|15[0-9]|16[0-9]|17[0-9]|18[0-9]|19[0-9])\d{8}$/.test(
      p,
    );
  };

  const handlePasswordChange = (psw, confirm = false) => {
    if (confirm) {
      setUserForm({ ...userForm, passwordConfirm: psw });
    } else {
      setUserForm({ ...userForm, password: psw });
    }
    if (confirm && psw !== userForm.password) {
      setError("两次密码输入不一致");
      return;
    }
    // 执行密码格式校验
    if (!validatePassword(psw)) {
      setError("密码必须包含至少6位字母和数字的组合");
    } else {
      setError("");
    }
  };

  const validatePassword = (psw) => {
    return /^(?=.*[A-Za-z])(?=.*\d).{6,}$/.test(psw);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userForm.name || !userForm.password || !userForm.passwordConfirm) {
      setError("必填字段都必须填写");
      return;
    }
    if (customerType && (!userForm.abbreviation || !userForm.salesperson)) {
      setError("需要填写简称和分配业务员");
      return;
    }
    if (!customerType && !userForm.role) {
      setError("需要选择角色");
      return;
    }
    if (userForm.password !== userForm.passwordConfirm) {
      setError("密码和确认密码不匹配");
      return;
    }
    if (!validatePassword(userForm.password)) {
      setError("密码必须包含至少6位字母和数字的组合");
      return;
    }
    if (userForm.email && !validateEmail(userForm.email)) {
      setError("请输入有效的邮箱地址");
      return;
    }
    if (userForm.phone && !validatePhone(userForm.phone)) {
      setError("请输入有效的手机号");
      return;
    }
    try {
      const encryptedPassword = md5(userForm.password);
      const updatedForm = {
        ...userForm,
        password: encryptedPassword,
        passwordConfirm: encryptedPassword,
      };
      const response = await request.put(BackendAPI.createUser, updatedForm);
      setUserForm(updatedForm);
      // clear form
      if (response.success) {
        toast("用户创建成功");
        setUserForm({ ...defaultForm, customer: customerType });
      } else {
        setUserForm({ ...userForm, password: "", passwordConfirm: "" });
      }
    } catch (error) {
      console.error("Error create user:", error);
      setError(`用户创建失败: ${error}`);
      setUserForm({ ...userForm, password: "", passwordConfirm: "" });
    }
  };

  const roleSelectOptions = useMemo(
    () =>
      USER_ROLE_STAFF_CREATABLE.map((role) => (
        <MenuItem key={role} value={role}>
          {UserRole[role]}
        </MenuItem>
      )),
    [],
  );

  const orgSelectOptions = useMemo(() => {
    const orgs = ["米狗科技健康（深圳）有限公司", "米狗实业(深圳)有限公司"];
    return orgs.map((org) => {
      return <MenuItem value={org}>{org}</MenuItem>;
    });
  }, []);

  return (
    <div className="create-user-form">
      <FormControl>
        <FormLabel id="select-create-user-type">用户类型</FormLabel>
        <RadioGroup
          defaultValue="customer"
          name="create-user-type"
          onChange={handleChangeType}
          row
        >
          <FormControlLabel
            value="customer"
            control={<Radio />}
            label="添加客户"
          />
          <FormControlLabel
            value="staff"
            disabled={!authContext?.isAdmin()}
            control={<Radio />}
            label="添加员工"
          />
        </RadioGroup>
      </FormControl>
      <p>请输入用户信息</p>
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <TextField
          required
          label={customerType ? "客户公司全称" : "员工姓名"}
          name="newname"
          value={userForm.name}
          onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
        />
        <TextField
          required
          label={customerType ? "客户编码" : "员工编码"}
          name="newcode"
          value={userForm.code}
          onChange={(e) => setUserForm({ ...userForm, code: e.target.value })}
        />
        {!customerType && (
          <FormControl sx={{ minWidth: 200 }} required>
            <InputLabel>角色</InputLabel>
            <Select
              required
              label="角色"
              name="role"
              value={userForm.role}
              onChange={(e) =>
                setUserForm({ ...userForm, role: e.target.value })
              }
              autoWidth
            >
              {roleSelectOptions}
            </Select>
          </FormControl>
        )}
        <TextField
          label="电子邮箱"
          name="newemail"
          value={userForm.email}
          onChange={(e) => handleEmailChange(e.target.value)}
        />
        <TextField
          label="电话号码"
          name="newphone"
          value={userForm.phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
        />
        <TextField
          required
          label="密码"
          name="newpassword"
          type="password"
          value={userForm.password}
          onChange={(e) => handlePasswordChange(e.target.value)}
        />
        <TextField
          required
          label="请确认密码"
          name="newpassword-confirm"
          type="password"
          value={userForm.passwordConfirm}
          onChange={(e) => handlePasswordChange(e.target.value, true)}
        />
        {customerType && (
          <TextField
            label="简称"
            name="newabbreviation"
            value={userForm.abbreviation}
            onChange={(e) =>
              setUserForm({ ...userForm, abbreviation: e.target.value })
            }
          />
        )}
        {customerType && (
          <FormControl sx={{ minWidth: 200 }} key={"sales-select"} required>
            <Autocomplete
              id={`sales-label`}
              options={salesOptions}
              onChange={(e, value: any) =>
                setUserForm({ ...userForm, salesperson: value })
              }
              renderInput={(params) => (
                <TextField required {...params} label={`分配业务员`} />
              )}
            />
          </FormControl>
        )}
        {customerType && (
          <TextField
            required
            label="分组（地区）"
            name="group"
            value={userForm.group}
            onChange={(e) =>
              setUserForm({ ...userForm, group: e.target.value })
            }
          />
        )}
        {customerType && (
          <FormControl sx={{ minWidth: 200 }} required>
            <InputLabel>使用组织</InputLabel>
            <Select
              required
              label="使用组织"
              name="organization"
              value={userForm.organization}
              onChange={(e) =>
                setUserForm({ ...userForm, organization: e.target.value })
              }
              autoWidth
            >
              {orgSelectOptions}
            </Select>
          </FormControl>
        )}
        {customerType && (
          <TextField
            required
            label="收款条件"
            name="paymentTerms"
            value={userForm.paymentTerms}
            onChange={(e) =>
              setUserForm({ ...userForm, paymentTerms: e.target.value })
            }
          />
        )}
        {customerType && (
          <TextField
            required
            label="地址"
            name="address"
            value={userForm.address}
            onChange={(e) =>
              setUserForm({ ...userForm, address: e.target.value })
            }
          />
        )}
        {error && <div className="create-user-form error-message">{error}</div>}
        <Button className="submit-button" type="submit" variant="contained">
          保存更改
        </Button>
      </Box>
    </div>
  );
};

export default CreateUser;
